import React, { useState } from "react";
import {
  TASKS,
  RAID_EVENTS,
  BUFFER_EVENT,
  END_OF_DAY_EVENT,
  STARTING_FOCUS,
  BLOCK_COUNT,
} from "./data.js";

const taskById = Object.fromEntries(TASKS.map((t) => [t.id, t]));

export default function RaidStage({ formation, onComplete }) {
  const [blockIndex, setBlockIndex] = useState(0);
  const [phase, setPhase] = useState("blocks"); // blocks | endOfDay
  const [focus, setFocus] = useState(STARTING_FOCUS);
  const [shieldsRemaining, setShieldsRemaining] = useState(formation.shieldsCount);
  const [log, setLog] = useState([]);
  const [resolved, setResolved] = useState(null);

  const currentTaskId = phase === "blocks" ? formation.blocks[blockIndex] : null;
  const currentTask = currentTaskId ? taskById[currentTaskId] : null;
  const isBuffer = phase === "blocks" && !currentTaskId;
  const event = phase === "endOfDay" ? END_OF_DAY_EVENT : isBuffer ? BUFFER_EVENT : RAID_EVENTS[currentTaskId];

  const choices = event?.choices || [];
  const canDeflect = phase === "blocks" && !isBuffer && event?.distraction && shieldsRemaining > 0;

  const allChoices = canDeflect
    ? [
        ...choices,
        {
          id: "shield",
          label: "Deflect using your prepared environment (uses 1 Focus Shield)",
          tag: "good",
          focusDelta: 0,
          result: "Your focus environment absorbed the interruption before it could land.",
          isShield: true,
        },
      ]
    : choices;

  const applyChoice = (choice) => {
    if (choice.isShield) setShieldsRemaining((s) => s - 1);
    setFocus((f) => Math.max(0, Math.min(100, f + (choice.focusDelta || 0))));
    setLog((prev) => [...prev, { title: event.title, choice }]);
    setResolved(choice);
  };

  const advance = () => {
    setResolved(null);
    if (phase === "blocks") {
      if (blockIndex + 1 < BLOCK_COUNT) {
        setBlockIndex((i) => i + 1);
      } else {
        setPhase("endOfDay");
      }
    } else {
      onComplete({ focus, log, shieldsUsed: formation.shieldsCount - shieldsRemaining });
    }
  };

  return (
    <div>
      {/* Focus meter */}
      <div className="mb-5">
        <div className="mb-1 flex items-center justify-between font-display text-[9px] text-mist/60">
          <span>FOCUS</span>
          <span>{focus}/100</span>
        </div>
        <div className="h-2.5 w-full border border-black bg-ink">
          <div
            className={`h-full transition-all duration-500 ${
              focus > 60 ? "bg-lime" : focus > 30 ? "bg-gold" : "bg-danger"
            }`}
            style={{ width: `${focus}%` }}
          />
        </div>
      </div>

      <div className="mb-4 flex items-center justify-between font-display text-[9px] text-mist/50">
        <span>{phase === "endOfDay" ? "END OF DAY" : `BLOCK ${blockIndex + 1}/${BLOCK_COUNT}`}</span>
        {shieldsRemaining > 0 && <span className="text-cyan">🛡️ {shieldsRemaining}</span>}
      </div>

      <div className="animate-popIn mb-5 border-2 border-black bg-ink p-5">
        {currentTask && (
          <p className="mb-2 text-[10px] uppercase text-mist/40">
            {blockIndex === 0 ? "🔥 Frog Block · " : ""}
            {currentTask.description}
          </p>
        )}
        <p className="pixel-heading mb-3 text-sm text-mist">{event.title}</p>
        <p className="text-sm leading-relaxed text-mist/80">{event.text}</p>
      </div>

      {!resolved ? (
        isBuffer ? (
          <button onClick={advance} className="btn-cyan w-full">
            CONTINUE ▶
          </button>
        ) : (
          <div className="space-y-2">
            {allChoices.map((choice) => (
              <button
                key={choice.id}
                onClick={() => applyChoice(choice)}
                className="w-full border-2 border-mist/30 px-4 py-3 text-left text-xs text-mist transition-colors hover:border-cyan hover:text-cyan"
              >
                {choice.label}
              </button>
            ))}
          </div>
        )
      ) : (
        <div
          className={`animate-popIn border-2 p-4 text-center ${
            resolved.tag === "good"
              ? "border-lime bg-lime/10 text-lime"
              : resolved.tag === "bad"
              ? "border-danger bg-danger/10 text-danger"
              : "border-gold bg-gold/10 text-gold"
          }`}
        >
          <p className="mb-3 text-xs">{resolved.result}</p>
          <button onClick={advance} className="btn-cyan !text-[9px]">
            CONTINUE ▶
          </button>
        </div>
      )}
    </div>
  );
}
